import React from "react";
import { Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";

/**
 * Read-only, compact renderer for the `special_items` JSON produced by
 * SpecialItemsWizard (Möbel / Baumaterial / Reifen). Used in:
 *  - DeliverySummary.jsx (customer-facing review step before submit)
 *  - can also be reused as-is in the admin transport detail page.
 */
const SpecialItemsSummary = ({ data }) => {
  const { t } = useTranslation();

  if (!data) return null;

  if (data.move_type === "Moebel_Einzelne_Gegenstaende" || data.move_type === "Umzug") {
    const byArea = {};
    (data.items || []).forEach((i) => {
      byArea[i.area] = byArea[i.area] || [];
      byArea[i.area].push(i);
    });

    return (
      <div className="mt-2">
        {data.scope && (
          <div className="mb-2">
            <Badge bg="primary" className="me-2 px-3 py-2 fs-6">
              {data.scope}
            </Badge>
            {data.has_special_transport && (
              <Badge bg="warning" text="dark" className="px-2 py-2">
                {t("specialItems.furniture.special_transport_badge", "Sondertransport")}
              </Badge>
            )}
          </div>
        )}

        <div className="mb-2">
          {(data.areas || []).map((a) => (
            <Badge bg="light" text="dark" className="border me-1 mb-1" key={a}>
              {a}
            </Badge>
          ))}
        </div>

        {Object.entries(byArea).map(([area, list]) => (
          <div key={area} className="mb-3 border-bottom pb-2">
            <div className="fw-bold text-dark">{area}</div>
            <ul className="list-unstyled ps-2 mb-1">
              {list.map((i, idx) => (
                <li key={idx} className="small py-1">
                  <span className="fw-semibold text-primary">{i.quantity} × </span>
                  <strong>{i.item}</strong>
                  {i.type ? <span className="text-muted"> ({i.type})</span> : ""}
                  {i.size ? <span className="text-muted"> [{i.size}]</span> : ""}
                  {i.description ? <span className="fst-italic text-secondary"> — {i.description}</span> : ""}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {data.heavy_items?.length > 0 && (
          <div className="mb-3 border-bottom pb-2">
            <div className="fw-bold text-dark">
              {t("specialItems.summary.heavy_items", "Besonders schwere oder empfindliche Gegenstände")}
            </div>
            <ul className="list-unstyled ps-2 mb-1">
              {data.heavy_items.map((i, idx) => (
                <li key={idx} className="small py-1">
                  <span className="fw-semibold text-danger">{i.quantity} × </span>
                  <strong>{i.item}</strong>
                  {i.type ? <span className="text-muted"> ({i.type})</span> : ""}
                  {i.size ? <span className="text-muted"> [{i.size}]</span> : ""}
                  {i.description ? <span className="fst-italic text-secondary"> — {i.description}</span> : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.assembly_service?.answer === "Ja" && (
          <div className="mb-2 border-bottom pb-2">
            <div className="fw-bold text-dark">
              {t("specialItems.summary.assembly_service", "Ab- und Aufbauservice")}
            </div>
            {data.assembly_service.items?.length > 0 ? (
              <ul className="list-unstyled ps-2 mb-1">
                {data.assembly_service.items.map((i, idx) => (
                  <li key={idx} className="small py-1">
                    <span className="fw-semibold text-success">{i.quantity || 1} × </span>
                    <strong>{i.item}</strong>
                    {i.service ? ` — ${i.service}` : ""}
                    {i.description ? ` (${i.description})` : ""}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="small text-muted ps-2">
                {t("specialItems.furniture.assembly_requested", "Service gewünscht")}
              </div>
            )}
            {data.kitchen_service && (
              <div className="small ps-2 mt-1 bg-light p-2 rounded">
                <strong>{t("specialItems.furniture.kitchen_service", "Küche")}: </strong>
                {data.kitchen_service.service}
                {data.kitchen_service.description ? ` (${data.kitchen_service.description})` : ""}
              </div>
            )}
          </div>
        )}

        {data.assembly_service?.answer === "Ich bin nicht sicher" && data.assembly_service.notes && (
          <div className="mb-2 border-bottom pb-2">
            <div className="fw-bold text-dark">
              {t("specialItems.summary.assembly_service", "Ab- und Aufbauservice")}
            </div>
            <div className="small text-muted ps-2">
              <strong>{t("specialItems.furniture.notes", "Hinweis")}:</strong> {data.assembly_service.notes}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (data.move_type === "Baumaterial") {
    return (
      <div className="mt-2">
        <ul className="small">
          {(data.materials || []).map((m, idx) => (
            <li key={idx}>
              {m.item}
              {m.quantity ? ` — ${m.quantity}× ${m.unit}` : ""}
              {m.total_weight_kg ? `, ${m.total_weight_kg}` : ""}
            </li>
          ))}
        </ul>
        {data.total_weight_estimate && (
          <div className="small text-muted">
            {t("specialItems.summary.total_weight")}: {data.total_weight_estimate}
          </div>
        )}
        {data.manual_review_required && (
          <Badge bg="warning" text="dark" className="mt-1">
            {t("specialItems.summary.manual_review")}
          </Badge>
        )}
      </div>
    );
  }

  if (data.move_type === "Reifen") {
    return (
      <div className="mt-2">
        <ul className="small">
          {(data.tires || []).map((tire, idx) => (
            <li key={idx}>
              {tire.quantity} × {tire.item}
              {tire.on_rims ? `, ${t("specialItems.tires.on_rims")}: ${tire.on_rims}` : ""}
              {tire.size ? `, ${t("specialItems.tires.size")}: ${tire.size}` : ""}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
};

export default SpecialItemsSummary;